import type { AdminFeatureFlagData, FeatureFlagKey } from '@workspace/api-backend'
import { Switch } from '@workspace/ui/components/switch'
import { cn } from '@workspace/ui/lib/utils'
import { X } from 'lucide-react'
import {
  useAddFeatureFlagInvestorMutation,
  useAddFeatureFlagStartupMutation,
  useRemoveFeatureFlagInvestorMutation,
  useRemoveFeatureFlagStartupMutation,
  useSetFeatureFlagEnabledMutation,
} from '../hooks/use-admin-feature-flags'
import { FEATURE_FLAG_META } from '../meta'
import { InvestorSearchPicker } from './investor-search-picker'
import { StartupSearchPicker } from './startup-search-picker'

interface FeatureFlagCardProps {
  flag: AdminFeatureFlagData
}

export function FeatureFlagCard({ flag }: FeatureFlagCardProps) {
  const meta = FEATURE_FLAG_META[flag.key]
  const setEnabled = useSetFeatureFlagEnabledMutation()
  const addStartup = useAddFeatureFlagStartupMutation()
  const removeStartup = useRemoveFeatureFlagStartupMutation()
  const addInvestor = useAddFeatureFlagInvestorMutation()
  const removeInvestor = useRemoveFeatureFlagInvestorMutation()
  const forInvestors = meta.audience === 'investors'
  const allowed = forInvestors ? flag.allowedInvestors : flag.allowedStartups
  const noun = forInvestors ? 'investor' : 'startup'

  const allowlistInert = flag.enabled
  const updatedAt = new Date(flag.updatedAt)

  const handleToggle = (next: boolean) => {
    setEnabled.mutate({ path: { key: flag.key as FeatureFlagKey }, body: { enabled: next } })
  }

  const handleAdd = (startup: { id: string }) => {
    addStartup.mutate({
      path: { key: flag.key as FeatureFlagKey },
      body: { startupId: startup.id },
    })
  }

  const handleRemove = (id: string) => {
    if (forInvestors) {
      removeInvestor.mutate({ path: { key: flag.key as FeatureFlagKey, investorId: id } })
    } else {
      removeStartup.mutate({ path: { key: flag.key as FeatureFlagKey, startupId: id } })
    }
  }

  const handleAddInvestor = (investor: { id: string }) => {
    addInvestor.mutate({
      path: { key: flag.key as FeatureFlagKey },
      body: { investorId: investor.id },
    })
  }

  const removing = forInvestors ? removeInvestor.isPending : removeStartup.isPending

  return (
    <div className="rounded-2xl border border-border bg-card/40 p-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            {flag.key}
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-foreground">
            {meta.title}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">{meta.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span
            className={cn(
              'text-xs font-medium',
              flag.enabled ? 'text-emerald-400' : 'text-muted-foreground'
            )}
          >
            {flag.enabled ? 'Global ON' : 'Global OFF'}
          </span>
          <Switch
            checked={flag.enabled}
            onCheckedChange={handleToggle}
            disabled={setEnabled.isPending}
          />
        </div>
      </div>

      <div className={cn('mt-6 border-t border-border pt-5', allowlistInert && 'opacity-50')}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-foreground">Allowlist</h3>
          <span className="text-xs text-muted-foreground">
            {allowed.length} {allowed.length === 1 ? noun : `${noun}s`}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {allowlistInert
            ? 'Global access is on — allowlist is ignored.'
            : `Only ${noun}s in this list have access while the flag is off.`}
        </p>

        {allowed.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {allowed.map((startup) => (
              <span
                key={startup.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/40 py-1 pl-3 pr-1 text-xs text-foreground"
              >
                {startup.name}
                <button
                  type="button"
                  onClick={() => handleRemove(startup.id)}
                  disabled={removing || allowlistInert}
                  className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-elevated hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={`Remove ${startup.name}`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-3">
          {forInvestors ? (
            <InvestorSearchPicker
              excludeIds={allowed.map((i) => i.id)}
              onSelect={handleAddInvestor}
              disabled={allowlistInert || addInvestor.isPending}
            />
          ) : (
            <StartupSearchPicker
              excludeIds={allowed.map((s) => s.id)}
              onSelect={handleAdd}
              disabled={allowlistInert || addStartup.isPending}
            />
          )}
        </div>
      </div>

      <div className="mt-4 text-[11px] text-muted-foreground">
        Updated {updatedAt.toLocaleString()}
      </div>
    </div>
  )
}
